import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { NavigateOptions, To, useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import classes from '../SearchResults.module.scss'

import { ButtonText, Heading, MultiSlideCarousel, Text } from '@percent/lemonade'

export type SearchResultsSectionProps = Readonly<{
  title: string
  noResultsText: string
  totalResults: number
  isFetching: boolean
  items: { id: string; content: ReactNode }[]
  skeleton: ReactNode
  seeAllTo: To
  seeAllNavigateOptions?: NavigateOptions
  testId?: string
  children?: ReactNode
}>

export function SearchResultsSection({
  title,
  noResultsText,
  totalResults,
  isFetching,
  items,
  skeleton,
  seeAllTo,
  seeAllNavigateOptions,
  testId,
  children
}: SearchResultsSectionProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()

  return (
    <div className={classes.ContentWrapper} data-testid={testId}>
      {!isFetching && items.length === 0 ? (
        <div className={classes.EmptyHeader}>
          <Heading level="h3" className={classes.Title}>
            {title}
          </Heading>
        </div>
      ) : (
        <MultiSlideCarousel
          title={title}
          titleClassName={classes.Title}
          navWrapperClassName={classes.NavWrapper}
          description={
            totalResults > 0 && !isFetching ? (
              <ButtonText onClick={() => navigate(seeAllTo, ...(seeAllNavigateOptions ? [seeAllNavigateOptions] : []))}>
                {t('workplace_giving.common.seeAll')}
              </ButtonText>
            ) : null
          }
          items={isFetching ? [{ id: 'loader', content: skeleton }] : items}
          itemsPerPage={{ xs: 2, md: 3, lg: 4, xl: 5 }}
          slideGap="1.5rem"
        />
      )}

      {totalResults === 0 ? (
        <Text className={classes.NoResultsText} size="small" color={theme.semantic.contentSecondary}>
          {noResultsText}
        </Text>
      ) : null}

      {children}
    </div>
  )
}
